import { Component } from '@theme/component';

/**
 * Bulk quote form (sections/dc-bulk.liquid).
 * - Prefills from the URL: ?kit=<id> or ?product=<handle>&qty=<n> pick the matching
 *   product option (by data-key) and quantity range; qty also seeds the notes.
 * - Remembers a short summary on submit so the thank-you state can offer a WhatsApp
 *   message that carries the enquiry (Shopify's contact form doesn't echo it back).
 * - Moves focus to the success message or the error list after the page reloads.
 *
 * @typedef {{ product?: HTMLSelectElement, quantity?: HTMLSelectElement, notes?: HTMLTextAreaElement,
 *   success?: HTMLElement, successWhatsapp?: HTMLAnchorElement, errors?: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class DcBulkFormComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  static #storageKey = 'dc-bulk-enquiry';

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { success, errors } = this.refs;

    if (success) {
      this.#prepareSuccess();
      success.focus();
      return;
    }
    if (errors) errors.focus();

    this.#prefill();
    this.querySelector('form')?.addEventListener('submit', this.#remember, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #prefill() {
    const { product, quantity, notes } = this.refs;
    const params = new URL(window.location.href).searchParams;
    const key = (params.get('kit') || params.get('product') || '').toLowerCase();
    const qty = Number(params.get('qty'));

    if (product && key) {
      const option = [...product.options].find((o) => (o.dataset.key || '').toLowerCase() === key);
      if (option) product.value = option.value;
    }

    if (quantity && Number.isFinite(qty) && qty > 0) {
      const match = [...quantity.options].find((o) => rangeContains(o.value, qty));
      if (match) quantity.value = match.value;
      if (notes && !notes.value.trim()) notes.value = `${qty} pcs`;
    }
  }

  #remember = () => {
    const form = this.querySelector('form');
    if (!form) return;
    const data = new FormData(form);
    const placements = [...data.keys()]
      .filter((k) => k.startsWith('contact[Placement: '))
      .map((k) => k.slice('contact[Placement: '.length, -1));
    const summary = [
      data.get('contact[name]'),
      data.get('contact[Company]'),
      [data.get('contact[Product]'), data.get('contact[Quantity]')].filter(Boolean).join(' · '),
      placements.join(', '),
    ]
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)
      .join('\n');
    try {
      sessionStorage.setItem(DcBulkFormComponent.#storageKey, summary);
    } catch {
      // Private mode: the generic WhatsApp message still works.
    }
  };

  #prepareSuccess() {
    const link = this.refs.successWhatsapp;
    if (!link) return;
    let summary = '';
    try {
      summary = sessionStorage.getItem(DcBulkFormComponent.#storageKey) ?? '';
      sessionStorage.removeItem(DcBulkFormComponent.#storageKey);
    } catch {
      return;
    }
    if (!summary) return;
    const url = new URL(link.href);
    const base = url.searchParams.get('text') ?? '';
    url.searchParams.set('text', `${base}\n\n${summary}`.trim());
    link.href = url.toString().replace(/\+/g, '%20');
  }
}

/**
 * "30–50" contains 40; "500+" contains anything from 500.
 * @param {string} label
 * @param {number} n
 */
function rangeContains(label, n) {
  const numbers = (label.match(/\d+/g) ?? []).map(Number);
  if (label.includes('+') && numbers.length === 1) return n >= numbers[0];
  if (numbers.length >= 2) return n >= numbers[0] && n <= numbers[1];
  return false;
}

if (!customElements.get('dc-bulk-form-component')) {
  customElements.define('dc-bulk-form-component', DcBulkFormComponent);
}
